import React from "react";
import { ChevronRight } from "lucide-react";
import type { ScheduleAuditLog } from "../../../../types";
import { AuditChanges } from "./AuditChanges";
import {
    SOURCE_LABELS,
    formatFullDateTime,
    formatShiftDateTime,
    formatTime,
    getActionMeta,
    isPublished,
} from "./auditUtils";

export interface AuditEntryProps {
    log: ScheduleAuditLog;
    timezone?: string;
    /** show employee + shift line (period view); single-schedule view already shows it in the header */
    showEmployee?: boolean;
    onViewHistory?: (log: ScheduleAuditLog) => void;
    isLast?: boolean;
}

const Chip: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "bg-gray-100 text-gray-600" }) => (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium leading-none ${className}`}>{children}</span>
);

export const AuditEntry: React.FC<AuditEntryProps> = ({ log, timezone, showEmployee = false, onViewHistory, isLast = false }) => {
    const meta = getActionMeta(log.action, log.action_label);
    const published = isPublished(log.was_published);
    const source = log.source ? SOURCE_LABELS[String(log.source).toLowerCase()] || log.source : null;
    const isRequest = log.action === "publish_request";
    const shiftText = log.shift?.title
        ? `${log.shift.title}${log.shift.start ? ` · ${formatShiftDateTime(log.shift.start)}` : ""}`
        : "";

    return (
        <div className={`relative flex gap-3 ${isLast ? "" : "pb-5"}`}>
            <div className="flex flex-col items-center">
                <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${meta.dot}`} />
                {!isLast && <span className="mt-1 w-px flex-1 bg-gray-200" />}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">{meta.label}</p>
                    <time
                        className="flex-shrink-0 text-xs text-gray-500"
                        title={formatFullDateTime(log.created, timezone)}
                        dateTime={log.created}
                    >
                        {formatTime(log.created, timezone)}
                    </time>
                </div>

                {showEmployee && !isRequest && (log.employee_name || shiftText) && (
                    <p className="mt-0.5 truncate text-xs text-gray-700">
                        {log.employee_name || "Unknown employee"}
                        {shiftText && <span className="text-gray-500"> · {shiftText}</span>}
                    </p>
                )}

                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                    <span>by {log.actor_name || log.actor_id || "Unknown"}</span>
                    {source && <Chip>{source}</Chip>}
                    {published === true && <Chip className="bg-green-50 text-green-700">Published</Chip>}
                    {published === false && !isRequest && <Chip className="bg-amber-50 text-amber-700">Draft</Chip>}
                </div>

                <AuditChanges log={log} />

                {onViewHistory && log.os_emp_schedules_id && (
                    <button
                        type="button"
                        onClick={() => onViewHistory(log)}
                        className="mt-2 inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                        View schedule history
                        <ChevronRight size={14} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default AuditEntry;
